import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../config";
// The money arithmetic and the size list are shared with HostEstimator.js --
// two copies of monthlyCents would be two answers to the same question.
import { SIZES, money, monthlyCents } from "./estimatorShared";
import "../../styles/Estimator.css";

// "What's your wall worth?" -- the address-taking estimator, at /estimate.
//
// NOT the host funnel's lead any more. That is HostEstimator.js, embedded in
// the hero of Become a host: four dropdowns, no address, nothing to type. This
// one survives as the variant for somebody who DOES want the road factored in,
// and is linked from How it works. Both POST the same endpoint and share their
// arithmetic through estimatorShared.js.
//
// If the address-based path stops earning its keep, this file and the
// /estimate route are what to delete; nothing else depends on them.
//
// The address someone types here is exactly what the waitlist wants, so the
// result ends by offering to carry it there. Nothing is stored until they
// choose that: an estimate is a marketing page, and trying numbers on one
// should not create a record of you.
//
// --- the honesty constraints, which are structural, not disclaimer text ----
//
//   * A RANGE is shown, never a single figure. The inputs -- a road's vehicle
//     count, a screen size, a venue category -- do not support a point
//     estimate, and one would read as a quote.
//   * The monthly figure is shown WITH its occupancy assumption in the same
//     breath. Daily times thirty is what a screen earns if it is booked every
//     day, which no screen is.
//   * Traffic is labelled as vehicles, never as viewers.
//
// --- what this page asks for, and why it changed ---------------------------
//
// It used to ask for an address, a screen size and a venue type, and priced
// almost entirely off AADT -- vehicle counts on the nearest road. That was a
// defensible proxy when the product was a screen facing that road. It was a
// weak one for a screen in a salon waiting area, where the value comes from
// how many people come in and how long they stay, neither of which is implied
// by the traffic driving past outside. A busy studio on a quiet side street
// was systematically under-quoted.
//
// It now asks the host the two questions directly, and the server prices off
// them (utils/estimate.py, the `footfall` basis). The road is still read, and
// still counts -- a busy road does correlate with a busy district -- but it is
// damped to a nudge once footfall is known.
//
// Both audience fields stay OPTIONAL. Somebody who does not know their weekly
// numbers still gets an answer, off the road alone, and the disclaimer the
// server returns says which of the two happened. A required field here would
// turn "I am curious what my salon could earn" into a form somebody abandons.
//
// The visitor figure is the host's own estimate and nothing measures it, so it
// is labelled as such here and everywhere it is later shown to an advertiser.
// It also raises what their screen can charge, which is a standing incentive
// to inflate it -- see the note beside the field.

const DEFAULT_VENUES = [
    { value: "salon", label: "Salon, barbershop or nail salon" },
    { value: "other", label: "Somewhere else" },
];

export default function EarningsEstimator() {
    const [address, setAddress] = useState("");
    const [inches, setInches] = useState(43);
    const [venue, setVenue] = useState("salon");
    // The two audience answers. Both optional to the API -- omitting the
    // visitor band drops the model back onto road traffic -- but the form asks
    // for them, because they are the inputs that actually describe an indoor
    // screen and the whole reason this page was re-modelled.
    const [visitors, setVisitors] = useState("");
    const [dwell, setDwell] = useState("");
    const [visitorBands, setVisitorBands] = useState([]);
    const [dwellBands, setDwellBands] = useState([]);
    const [disclosure, setDisclosure] = useState("");
    const [venues, setVenues] = useState(DEFAULT_VENUES);
    const [coverage, setCoverage] = useState(null);

    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    // The occupancy the host has dialled in. Seeded from the server's default
    // when a result arrives, then owned entirely by the slider -- so re-running
    // the estimate for a different screen size does not silently reset a
    // position the host deliberately chose.
    const [occupancy, setOccupancy] = useState(null);
    // {status: "checking" | "ok" | "coarse" | "none", match} -- what the
    // resolver made of what has been typed so far.
    const [check, setCheck] = useState(null);

    const [suggestions, setSuggestions] = useState([]);
    const [openList, setOpenList] = useState(false);
    const [active, setActive] = useState(-1);
    // Set when a suggestion is chosen, so the change it causes does not
    // immediately reopen the dropdown underneath the thing just picked.
    const pickedRef = useRef(false);
    const boxRef = useRef(null);

    // The venue list is served rather than hardcoded: the multipliers live in
    // the backend's estimate.py, and a form offering a choice the model does
    // not know would be silently mapped to "other".
    useEffect(() => {
        let alive = true;
        fetch(`${API_BASE_URL}/api/estimate/options`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (!alive || !d) return;
                if (Array.isArray(d.venue_types) && d.venue_types.length) {
                    setVenues(d.venue_types);
                }
                if (Array.isArray(d.visitor_bands)) setVisitorBands(d.visitor_bands);
                if (Array.isArray(d.dwell_bands)) setDwellBands(d.dwell_bands);
                if (d.visitors_disclosure) setDisclosure(d.visitors_disclosure);
                setCoverage(d.coverage || null);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    // Type-ahead, debounced at 300ms.
    //
    // The debounce is not a nicety. LocationIQ's free tier allows 2 requests
    // per SECOND account-wide -- not per visitor -- so a request per keystroke
    // would throttle the whole site as soon as two people typed at once. The
    // backend caches every prefix in Redis for a month, which is what makes
    // that limit survivable, and a throttled lookup returns an empty list
    // rather than an error.
    useEffect(() => {
        if (pickedRef.current) { pickedRef.current = false; return undefined; }
        const query = address.trim();
        // Mirrors looks_like_an_address() on the server: a digit and a space,
        // so we only spend a request on something that could resolve to a
        // street address. Raising a character count instead would cut the
        // short prefixes, which are the ones the server has cached.
        const worthAsking =
            query.length >= 5 && /\d/.test(query) && query.includes(" ");
        if (!worthAsking) { setSuggestions([]); return undefined; }
        let alive = true;
        const timer = setTimeout(async () => {
            try {
                const res = await fetch(
                    `${API_BASE_URL}/api/estimate/suggest?q=${encodeURIComponent(query)}`
                );
                if (!alive || !res.ok) return;
                const data = await res.json();
                setSuggestions(data.suggestions || []);
                setActive(-1);
                if ((data.suggestions || []).length) setOpenList(true);
            } catch {
                if (alive) setSuggestions([]);
            }
        }, 400);
        return () => { alive = false; clearTimeout(timer); };
    }, [address]);

    // Click-away closes the list.
    useEffect(() => {
        function onDown(e) {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false);
        }
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, []);

    function pick(item) {
        pickedRef.current = true;
        setAddress(item.label);
        setSuggestions([]);
        setOpenList(false);
        setActive(-1);
    }

    function onKeyDown(e) {
        if (!openList || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => (i + 1) % suggestions.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === "Enter" && active >= 0) {
            // Only swallows Enter when a row is highlighted, so Enter on a
            // typed address still submits the form.
            e.preventDefault();
            pick(suggestions[active]);
        } else if (e.key === "Escape") {
            setOpenList(false);
        }
    }

    // Verify on a pause, not on a keystroke.
    //
    // This is not autocomplete, and it is worth being clear why. There is no
    // free, keyless type-ahead for US addresses that is any good -- Photon is
    // keyless but ranks US streets badly (it answers "Main St Stroudsburg"
    // with bus stops), and Nominatim's usage policy forbids autocomplete
    // against its public instance. The US Census geocoder IS authoritative and
    // free, and it takes about a second, which buys a verify-on-pause rather
    // than a dropdown.
    //
    // What it gets us is arguably better than a dropdown anyway: the address
    // is echoed back in canonical form, spelling corrected, before anybody
    // submits. "2229 whitetail court stroudsburg pa 18360" comes back as
    // "2229 White Tail Ct, Stroudsburg, PA 18360".
    useEffect(() => {
        const query = address.trim();
        if (query.length < 8) {
            setCheck(null);
            return undefined;
        }
        let alive = true;
        setCheck({ status: "checking" });
        const timer = setTimeout(async () => {
            try {
                const res = await fetch(
                    `${API_BASE_URL}/api/estimate/verify?q=${encodeURIComponent(query)}`
                );
                if (!alive) return;
                if (!res.ok) { setCheck(null); return; }
                const data = await res.json();
                if (!data.match) {
                    setCheck({ status: "none" });
                } else if (!data.match.street_level) {
                    setCheck({ status: "coarse", match: data.match });
                } else {
                    setCheck({ status: "ok", match: data.match });
                }
            } catch {
                // Silent. This is a convenience on the way to a button that
                // does the real lookup; a red error under the field because a
                // background check timed out would be noise.
                if (alive) setCheck(null);
            }
        }, 550);
        return () => { alive = false; clearTimeout(timer); };
    }, [address]);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        setError("");
        setResult(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/estimate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    address,
                    screen_inches: inches,
                    venue_type: venue,
                    // Sent only when answered. An empty string would be a
                    // validation error; omitting it is the documented way to
                    // say "I do not know", and the server then prices off the
                    // road and says so in its disclaimer.
                    ...(visitors ? { visitors_band: visitors } : {}),
                    ...(dwell ? { dwell_band: dwell } : {}),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || "We could not work that one out.");
                return;
            }
            setResult(data);
            // Seed the slider from the server's default the FIRST time only.
            // A host who dragged it to 4 days and then changed the screen size
            // is still asking about 4 days; resetting under them would look
            // like the page arguing with their answer.
            setOccupancy((current) =>
                current === null
                    ? ((data.estimate || {}).assumptions || {}).occupancy ?? 0.25
                    : current
            );
        } catch {
            setError("Network error. Nothing was sent.");
        } finally {
            setBusy(false);
        }
    }

    const est = result && result.estimate;
    const traffic = result && result.traffic;
    // Null until the first result arrives, so the slider always has a number.
    const occ = occupancy === null ? 0.25 : occupancy;
    // The day count is the honest half of the control: "23% occupancy" is
    // jargon a salon owner has to translate, "about 7 days a month" is the
    // same fact in a form they can check against their own week.
    const bookedDays = est
        ? Math.round((est.assumptions.days_per_month || 30.4) * occ)
        : 0;

    return (
        <div className="est">
            <header className="est__head">
                {/* "What's your wall worth?" rather than "What could your space
                    earn?". The page answers a pricing question -- what a screen
                    in this room should list for -- and that is a claim we can
                    stand behind. "What could you earn" promises a revenue
                    forecast, which needs an occupancy rate WallBurst cannot
                    predict for anybody yet. The slider in the result is where
                    earnings get discussed, and there the host sets the
                    assumption rather than us. */}
                <h1 className="est__title">What’s your wall worth?</h1>
                <p className="est__lede">
                    Tell us about your place and we’ll suggest what a screen there
                    should list for — then you can work out what that adds up to.
                </p>
            </header>

            <form className="est__form" onSubmit={submit}>
                <label className="est__field est__field--wide">
                    <span className="est__label">Street address</span>
                    <div className="est__combo" ref={boxRef}>
                        <input
                            className="wb-input"
                            type="text"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            onKeyDown={onKeyDown}
                            onFocus={() => suggestions.length && setOpenList(true)}
                            placeholder="700 Main St, Stroudsburg, PA"
                            required
                            maxLength={300}
                            /* off, not "street-address": the browser's own
                               autofill panel would cover this list, and two
                               dropdowns fighting over one field is worse than
                               either alone. */
                            autoComplete="off"
                            role="combobox"
                            aria-expanded={openList && suggestions.length > 0}
                            aria-controls="est-suggestions"
                            aria-autocomplete="list"
                            aria-activedescendant={
                                active >= 0 ? `est-sug-${active}` : undefined
                            }
                        />
                        {openList && suggestions.length > 0 && (
                            <ul className="est__sugs" id="est-suggestions" role="listbox">
                                {suggestions.map((item, i) => (
                                    <li
                                        key={item.label + i}
                                        id={`est-sug-${i}`}
                                        role="option"
                                        aria-selected={i === active}
                                        className={
                                            "est__sug" + (i === active ? " is-active" : "")
                                        }
                                        /* mousedown, not click: click fires
                                           after blur, by which time the
                                           click-away handler has already
                                           closed the list. */
                                        onMouseDown={(e) => { e.preventDefault(); pick(item); }}
                                        onMouseEnter={() => setActive(i)}
                                    >
                                        {item.label}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {check && check.status === "ok" ? (
                        <span className="est__found">
                            ✓ {check.match.resolved}
                        </span>
                    ) : check && check.status === "coarse" ? (
                        <span className="est__coarse">
                            That is only placing at the town level. Add the house
                            number and ZIP — we read the traffic on your road, not
                            the middle of town.
                        </span>
                    ) : check && check.status === "none" ? (
                        <span className="est__coarse">
                            We can’t find that one yet. Check the house number and
                            include the ZIP.
                        </span>
                    ) : (
                        <span className="est__help">
                            {check && check.status === "checking"
                                ? "Checking that address…"
                                : "Include the house number, town and ZIP — we read the traffic on your road, not the middle of town."}
                        </span>
                    )}
                </label>

                <label className="est__field">
                    <span className="est__label">Screen size</span>
                    <select
                        className="wb-select"
                        value={inches}
                        onChange={(e) => setInches(Number(e.target.value))}
                    >
                        {SIZES.map((s) => (
                            <option key={s} value={s}>{s}&quot; diagonal</option>
                        ))}
                    </select>
                </label>

                <label className="est__field">
                    <span className="est__label">What kind of place is it?</span>
                    <select
                        className="wb-select"
                        value={venue}
                        onChange={(e) => {
                            const next = e.target.value;
                            setVenue(next);
                            // Pre-fill the dwell answer from the category, so
                            // the common case is one fewer decision. The
                            // default ships with each option from the server
                            // rather than being a second copy of the mapping
                            // here, which would drift the first time a venue
                            // type is retuned.
                            const picked = venues.find((v) => v.value === next);
                            if (picked && picked.default_dwell) setDwell(picked.default_dwell);
                        }}
                    >
                        {venues.map((v) => (
                            <option key={v.value} value={v.value}>{v.label}</option>
                        ))}
                    </select>
                </label>

                {/* The two questions that actually describe an indoor screen.
                    Optional: leaving the first blank prices off the road
                    instead, and the disclaimer under the result says so. */}
                <label className="est__field">
                    <span className="est__label">Roughly how many people a week?</span>
                    <select
                        className="wb-select"
                        value={visitors}
                        onChange={(e) => setVisitors(e.target.value)}
                    >
                        <option value="">I’m not sure</option>
                        {visitorBands.map((b) => (
                            <option key={b.value} value={b.value}>{b.label}</option>
                        ))}
                    </select>
                    <span className="est__help">
                        {visitors
                            /* The server's own wording, fetched with the bands,
                               so the caveat a host reads before answering is the
                               same sentence advertisers later see beside the
                               figure. Two hand-written copies would drift. */
                            ? `A rough number is fine. ${disclosure || "Estimated by the host, not measured."}`
                            : "Your best guess gives a far better answer than the road outside."}
                    </span>
                </label>

                <label className="est__field">
                    <span className="est__label">How long does a visit last?</span>
                    <select
                        className="wb-select"
                        value={dwell}
                        onChange={(e) => setDwell(e.target.value)}
                    >
                        <option value="">Use the usual for this kind of place</option>
                        {dwellBands.map((b) => (
                            <option key={b.value} value={b.value}>{b.label}</option>
                        ))}
                    </select>
                    <span className="est__help">
                        Time spent in front of the screen is worth more than
                        people passing it.
                    </span>
                </label>

                <div className="est__submit">
                    <button
                        type="submit"
                        className="wb-btn wb-btn--primary wb-btn--lg"
                        disabled={busy}
                    >
                        {busy ? "Looking it up…" : "Estimate my earnings"}
                    </button>
                </div>
            </form>

            {error && <p className="est__error">{error}</p>}

            {result && (
                <section className="est__result">
                    <p className="est__resolved">
                        {result.location.resolved}
                    </p>

                    {/* The headline is the DAY RATE, alone.
                        It used to sit beside a "Typical month" figure of equal
                        weight, and that was the wrong number to lead with: a
                        monthly total is a revenue forecast, and forecasting
                        revenue needs an occupancy rate that WallBurst cannot
                        yet predict for anybody. A day rate needs no such
                        assumption -- it is our pricing opinion about this room
                        and this screen, which is a claim we can stand behind.
                        It is also the number a host weighs when deciding. */}
                    <div className="est__headline">
                        <span className="est__figure-label">We’d suggest listing at</span>
                        <span className="est__headline-value">
                            {money(est.daily_rate_low_cents)}–{money(est.daily_rate_high_cents)}
                        </span>
                        <span className="est__figure-note">per day, when it’s booked</span>
                    </div>

                    {/* And the monthly figure becomes something the host drives.
                        This was a constant (0.35) asserted as fact and quietly
                        multiplied into the total -- the single least defensible
                        number on the page, doing more work than any other.
                        Handing it over is both more honest and more convincing:
                        a host who chooses 8 days themselves believes the answer
                        in a way they never believe one asserted at them. */}
                    <div className="est__occ">
                        <label className="est__occ-head" htmlFor="est-occupancy">
                            <span className="est__figure-label">
                                If it’s booked…
                            </span>
                            <span className="est__occ-days">
                                {bookedDays === 0
                                    ? "not at all"
                                    : `about ${bookedDays} ${bookedDays === 1 ? "day" : "days"} a month`}
                            </span>
                        </label>

                        <input
                            id="est-occupancy"
                            className="est__slider"
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={Math.round(occ * 100)}
                            onChange={(e) => setOccupancy(Number(e.target.value) / 100)}
                            aria-label="How many days a month the screen is booked"
                            aria-valuetext={
                                bookedDays === 0
                                    ? "Not booked at all"
                                    : `About ${bookedDays} days a month`
                            }
                        />
                        {/* The percentage is deliberately secondary to the day
                            count. "23% occupancy" is jargon a salon owner has to
                            translate; "about 7 days a month" is the same fact in
                            a form they can check against their own week. */}
                        <div className="est__occ-scale" aria-hidden="true">
                            <span>Never</span>
                            <span>{Math.round(occ * 100)}% of days</span>
                            <span>Every day</span>
                        </div>

                        <div className="est__occ-result">
                            <span className="est__figure-label">You’d make</span>
                            <span className="est__headline-value">
                                {bookedDays === 0 ? (
                                    "$0"
                                ) : (
                                    <>
                                        {money(monthlyCents(est.daily_rate_low_cents, occ, est.assumptions))}
                                        –
                                        {money(monthlyCents(est.daily_rate_high_cents, occ, est.assumptions))}
                                    </>
                                )}
                            </span>
                            <span className="est__figure-note">
                                a month, after our{" "}
                                {Math.round((1 - est.assumptions.vendor_share) * 100)}% fee
                            </span>
                        </div>

                        {/* Said at the top of the range rather than left implied.
                            A slider that goes to 100% will be dragged there, and
                            "every day of the month" is not something any screen
                            does or that WallBurst can currently sell. */}
                        {occ > 0.6 && (
                            <p className="est__occ-warn">
                                That is most days of the month. Worth treating as a
                                ceiling rather than a plan — we are still building
                                up advertisers in most areas.
                            </p>
                        )}
                    </div>

                    <ul className="est__factors">
                        {/* The audience factors come first when they drove the
                            number. Leading with road traffic under a figure
                            that was actually priced off the host's own footfall
                            answer would misdescribe our own arithmetic. */}
                        {est.factors.visitors && (
                            <li>
                                <span className="est__factor-k">Visitors</span>
                                <span className="est__factor-v">
                                    {est.factors.visitors.label}
                                    <span className="est__factor-note">
                                        {" · "}{est.factors.visitors.disclosure}
                                    </span>
                                </span>
                            </li>
                        )}
                        {est.factors.dwell && (
                            <li>
                                <span className="est__factor-k">Typical visit</span>
                                <span className="est__factor-v">
                                    {est.factors.dwell.label}
                                    {est.factors.dwell.source === "venue" && (
                                        <span className="est__factor-note">
                                            {" · typical for this kind of place"}
                                        </span>
                                    )}
                                </span>
                            </li>
                        )}
                        <li>
                            <span className="est__factor-k">Traffic</span>
                            <span className="est__factor-v">
                                {traffic ? (
                                    <>
                                        {traffic.aadt.toLocaleString()} vehicles a day
                                        {" · "}
                                        {est.factors.traffic.label}
                                        {/* Said plainly, because otherwise a
                                            host reads a "Main road" label and
                                            assumes it is doing most of the
                                            work. Once footfall is known it is
                                            not. */}
                                        {est.factors.traffic.damped && (
                                            <span className="est__factor-note">
                                                {" · counts for a little, now that we know who comes in"}
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <>No traffic survey covers this address</>
                                )}
                            </span>
                        </li>
                        <li>
                            <span className="est__factor-k">Screen</span>
                            <span className="est__factor-v">
                                {est.factors.size.label} · {result.screen.venue_label}
                            </span>
                        </li>
                    </ul>

                    {traffic && traffic.confidence !== "high" && (
                        // Said, because a segment found a kilometre away is a
                        // much weaker claim than one found next door, and the
                        // number looks identically confident either way.
                        <p className="est__caveat">
                            The nearest traffic count we could find is about{" "}
                            {traffic.radius_m}m away, so treat this as a rough guide.
                        </p>
                    )}

                    {!traffic && coverage && (
                        <p className="est__caveat">
                            Traffic counts come from {coverage.traffic_source}, which
                            covers {coverage.traffic_region}. Outside that we fall back
                            to typical rates for your screen and venue.
                        </p>
                    )}

                    <p className="est__disclaimer">{result.disclaimer}</p>

                    <div className="est__cta">
                        <p>{result.next_step.message}</p>
                        {/* Carries the address to Become a host, which reads
                            it from the query string and prefills the
                            application rather than asking again. */}
                        <Link
                            to={`/waitlist?address=${encodeURIComponent(address)}`}
                            className="wb-btn wb-btn--primary wb-btn--lg"
                        >
                            Apply to host
                        </Link>
                    </div>
                </section>
            )}

            {!result && (
                /* Says what the number is built from, because what it is built
                   from is no longer the thing being sold. A host reading this
                   should understand it as a locality signal, not as a
                   measurement of their own waiting room. */
                <p className="est__footnote">
                    A rough guide based on what you tell us about your place —
                    how many people come through, how long they stay, your screen
                    size and the kind of business. Visitor numbers are your own
                    estimate; nothing here counts them.
                    {coverage
                        ? ` We also read traffic near the address, from ${coverage.traffic_source}, which covers ${coverage.traffic_region}.`
                        : ""}
                </p>
            )}
        </div>
    );
}
